import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { clearSessionToken, getSessionToken } from '../lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If we're mid-OAuth callback (session_id or session_token in hash),
    // AuthCallback owns the flow - don't race it with a premature /me call.
    // Exception: if we already have a stored token, the /me call will succeed
    // immediately and avoids an unauthenticated flash on page load.
    const hash = window.location.hash || '';
    const isOAuthCallback = hash.includes('session_id=') || hash.includes('session_token=');
    if (isOAuthCallback && !getSessionToken()) {
      console.log('[AUTH] OAuth callback in progress (no token yet) - deferring check to AuthCallback');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      console.log('[AUTH] /me OK:', response.data?.email);
      setUser(response.data);
    } catch (error) {
      const status = error.response?.status;
      console.log(`[AUTH] /me failed (${status || 'network'}) - clearing user`);
      // Only clear the persisted token on explicit 401 - transient network
      // errors should leave the token in place so the next request can retry.
      if (status === 401) {
        clearSessionToken();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('[AUTH] Failed to get Google login URL:', error);
      window.location.href = '/';
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearSessionToken();
    setUser(null);
    window.location.href = '/';
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.log('[AUTH] refreshUser failed:', error.response?.status);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refreshUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
